import { i18n } from '@lingui/core';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { RheologyDataDto, RheologyDataSummaryDto, useRheologyDataQuery } from 'providers/api';
import React, { ChangeEvent } from 'react';

interface RheologyDataSetSelectorProps {
  mineModelId: string;
  rheologyDatasetList: RheologyDataSummaryDto[] | RheologyDataDto[];
  selectedRheologyDatasetId?: string;
  disabled?: boolean;
  onRheologyDataSetSelected: (rheologyData: any) => void;
  size?: 'small' | 'medium';
}

const RheologyDataSetSelector = ({
  mineModelId,
  rheologyDatasetList,
  selectedRheologyDatasetId,
  disabled = false,
  size = 'small',
  onRheologyDataSetSelected,
}: RheologyDataSetSelectorProps) => {
  const [selectedRheologyData, setSelectedRheologyData] = React.useState<RheologyDataSummaryDto | null>(null);

  useRheologyDataQuery(
    {
      mineModelId,
      rheologyDatasetId: selectedRheologyData && selectedRheologyData.rheologyDataSetId ? selectedRheologyData.rheologyDataSetId : '',
    },
    {
      enabled: !!selectedRheologyData,
      onSuccess: (result) => {
        result && onRheologyDataSetSelected(result);
      },
    },
  );

  // when a RheologyDataSummaryDto has been selected - download the specific RheologyDataDto
  const handleChange = (event: ChangeEvent<{}>, value: RheologyDataSummaryDto | null) => {
    setSelectedRheologyData(value);
  };

  const selectedRheologyDataTemp = (rheologyDatasetList as any).find(
    (rheologyDataItem: RheologyDataSummaryDto) => rheologyDataItem.rheologyDataSetId === selectedRheologyDatasetId,
  );

  return (
    <Autocomplete
      options={rheologyDatasetList}
      autoHighlight
      getOptionLabel={(option: any) => option.reference}
      value={selectedRheologyDataTemp}
      onChange={handleChange}
      fullWidth
      disabled={disabled}
      // Incomplete (draft) and deactivated (archived) datasets are still listed but cannot be
      // selected: incomplete until completed, deactivated until reactivated. Keeping them listed
      // (rather than hidden) lets recipes that already reference them keep displaying the value.
      getOptionDisabled={(option: any) => option.isComplete === false || option.isDeactivated === true}
      renderOption={(props, option: any) => {
        const { key, ...optionProps } = props;
        const isIncomplete = option.isComplete === false;
        const isDeactivated = option.isDeactivated === true;
        return (
          <Box
            key={key}
            sx={{
              borderRadius: '8px',
              margin: '5px',
              opacity: isIncomplete || isDeactivated ? 0.6 : 1,
            }}
            component="li"
            {...optionProps}
          >
            {`${option.reference || ''} ${option.version || ''}`}
            {isIncomplete && ` — ${i18n._('incomplete')}`}
            {!isIncomplete && isDeactivated && ` — ${i18n._('deactivated')}`}
          </Box>
        );
      }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={i18n._('Select a rheology dataset')}
          variant="outlined"
          fullWidth
          size={size}
          slotProps={{
            htmlInput: params.inputProps,
          }}
        />
      )}
    />
  );
};

export default RheologyDataSetSelector;
